import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { PlusCircle, Container } from 'lucide-react';
import { SavedConnectionsList } from './SavedConnectionsList';
import { NewConnectionPopup } from './NewConnectionPopup';
import { useConnectionStore } from '../store/connectionStore';
import { ImportSourcesMenu } from '../connection-import';
import { openDockerDiscoveryDialog } from '../docker-discovery';
import type { Connection, SaveConnectionOptions } from '../../../types/electron';

interface AvailableConnectionsPopupProps {
  onAddNew: () => void;
  onCancel?: () => void;
}

export function AvailableConnectionsPopup({
  onAddNew,
  onCancel,
}: AvailableConnectionsPopupProps) {
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const { updateConnection, deleteConnection } = useConnectionStore();

  const handleEdit = (connection: Connection) => {
    setEditingConnection(connection);
  };

  const handleSaveEdit = async (connection: Connection, options: SaveConnectionOptions): Promise<boolean> => {
    const success = await updateConnection(connection, options);
    if (success) {
      setEditingConnection(null);
    }
    return success;
  };

  const handleDelete = async (connectionId: string): Promise<boolean> => {
    const success = await deleteConnection(connectionId);
    if (success) {
      setEditingConnection(null);
    }
    return success;
  };

  const handleCancelEdit = () => {
    setEditingConnection(null);
  };

  // If editing a connection, show the edit dialog instead
  if (editingConnection) {
    return (
      <NewConnectionPopup
        onSave={handleSaveEdit}
        onCancel={handleCancelEdit}
        onDelete={handleDelete}
        editConnection={editingConnection}
      />
    );
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open && onCancel) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] !flex flex-col gap-2 overflow-hidden" data-testid="saved-connections-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Saved Connections</DialogTitle>
        </DialogHeader>

        <div className="py-2 flex-1 min-h-0 flex flex-col">
          <SavedConnectionsList
            onAddNew={onAddNew}
            showAddButton={false}
            onConnectSuccess={onCancel}
            onEdit={handleEdit}
          />
        </div>

        <DialogFooter className="flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onCancel} data-testid="cancel-connections-button">
              Cancel
            </Button>
            <ImportSourcesMenu
              entry="manage_dialog"
              onBeforeOpen={() => {
                if (onCancel) onCancel();
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (onCancel) onCancel();
                void openDockerDiscoveryDialog('manage_dialog');
              }}
              className="flex items-center gap-2 text-muted-foreground"
              data-testid="detect-docker-button"
            >
              <Container className="h-4 w-4" />
              Detect local db&apos;s
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={onAddNew}
            className="flex items-center gap-2 active:scale-[0.96] transition-[color,box-shadow,scale]"
            data-testid="add-new-connection-button"
          >
            <PlusCircle className="h-4 w-4" />
            Add New Connection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
